import { useState } from 'react';
import type { CSSProperties, ElementType, ComponentPropsWithoutRef, ReactNode } from 'react';

const PADS = {
  none: '0',
  sm: 'var(--space-3)',
  md: 'var(--space-5)',
  lg: 'var(--space-6)',
} as const;

const ELEV = {
  flat: { boxShadow: 'none', border: '1px solid var(--border)' },
  raised: { boxShadow: 'var(--shadow-sm)', border: '1px solid var(--border)' },
  floating: { boxShadow: 'var(--shadow-md)', border: '1px solid transparent' },
} as const satisfies Record<string, CSSProperties>;

export type CardProps<T extends ElementType = 'div'> = {
  children?: ReactNode;
  elevation?: keyof typeof ELEV;
  padding?: keyof typeof PADS;
  interactive?: boolean;
  as?: T;
  style?: CSSProperties;
} & Omit<ComponentPropsWithoutRef<T>, 'children' | 'style' | 'as'>;

/**
 * Card — the primary surface container. Soft, rounded, warm-shadowed.
 * Interactive cards add a gentle 2px lift + shadow bump on hover.
 */
export function Card<T extends ElementType = 'div'>({
  children,
  elevation = 'raised',
  padding = 'md',
  interactive = false,
  as,
  style,
  ...rest
}: CardProps<T>) {
  const [hover, setHover] = useState(false);
  const El = (as ?? 'div') as ElementType;

  const styles: CSSProperties = {
    background: 'var(--surface)',
    borderRadius: 'var(--radius-lg)',
    padding: PADS[padding] ?? PADS.md,
    color: 'var(--text)',
    transition:
      'box-shadow var(--duration-normal) var(--ease-soft), transform var(--duration-normal) var(--ease-soft)',
    ...ELEV[elevation],
    ...(interactive ? { cursor: 'pointer' } : null),
    ...(interactive && hover ? { boxShadow: 'var(--shadow-md)', transform: 'translateY(-2px)' } : null),
    ...style,
  };

  return (
    <El
      style={styles}
      onMouseEnter={interactive ? () => setHover(true) : undefined}
      onMouseLeave={interactive ? () => setHover(false) : undefined}
      {...rest}
    >
      {children}
    </El>
  );
}
